import { Spinner } from '@nextui-org/react'
import React, { useEffect, useRef, useState } from 'react'
import { InitialsAvatar } from '~/components/common/InitialsAvatar'
import { Typography } from '~/components/common/Typography'
import { ChatMarkdown } from '~/layouts/ChatDisplay'
import {
  ConversationCreditUsage,
  ConversationMessage,
  MessageBlock,
  getConversationCreditUsage,
  getConversationMessages
} from '~/services/superAdminService'

interface ParsedMessageContent {
  thinkingBlocks: { title: string }[]
  textContent: string | null
}

const parseMessageContent = (msg: ConversationMessage): ParsedMessageContent => {
  const result: ParsedMessageContent = {
    thinkingBlocks: [],
    textContent: null
  }

  if (msg.text && msg.text.trim()) {
    result.textContent = msg.text
    return result
  }

  if (msg.blocks && Array.isArray(msg.blocks)) {
    for (const block of msg.blocks as MessageBlock[]) {
      if (block.type === 'thinking' && block.text?.text) {
        result.thinkingBlocks.push({ title: block.text.text })
      } else if (block.type === 'text' && block.text?.text && !result.textContent) {
        const textContent = block.text.text.trim()
        if (textContent) {
          result.textContent = textContent
        }
      }
    }
  }

  return result
}

const hasRenderableContent = (msg: ConversationMessage): boolean => {
  const parsed = parseMessageContent(msg)
  return parsed.thinkingBlocks.length > 0 || parsed.textContent !== null
}

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function CreditUsageBanner({ usage }: { usage: ConversationCreditUsage }) {
  const fmt = (n: number) => n.toLocaleString()
  return (
    <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-xs'>
      <span className='font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded'>
        {usage.credits} credit{usage.credits !== 1 ? 's' : ''}
      </span>
      <span className='text-gray-500'>
        {fmt(usage.input_tokens)} in / {fmt(usage.output_tokens)} out tokens
      </span>
      <span className='text-gray-400'>
        ({usage.event_count} LLM call{usage.event_count !== 1 ? 's' : ''})
      </span>
      {usage.model_breakdown.length > 0 && (
        <span className='text-gray-400'>
          [{usage.model_breakdown.map(m => `${m.model}: ${m.raw_credits.toFixed(2)}cr`).join(', ')}]
        </span>
      )}
    </div>
  )
}

interface SuperAdminConversationDisplayProps {
  conversationId: number
  title?: string
}

export function SuperAdminConversationDisplay({
  conversationId,
  title
}: SuperAdminConversationDisplayProps) {
  const [messages, setMessages] = useState<ConversationMessage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [creditUsage, setCreditUsage] = useState<ConversationCreditUsage | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsLoading(true)
    setCreditUsage(null)

    Promise.all([
      getConversationMessages(conversationId),
      getConversationCreditUsage(conversationId).catch(() => null)
    ])
      .then(([result, credits]) => {
        setMessages(result.messages)
        setCreditUsage(credits)
      })
      .catch(() => {
        setMessages([])
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [conversationId])

  useEffect(() => {
    if (!isLoading && messages.length > 0 && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' })
    }
  }, [isLoading, messages])

  return (
    <div className='h-full flex flex-col'>
      <div className='shrink-0 border-b border-gray-200 px-6 py-4'>
        <div className='flex items-center justify-between'>
          <h2 className='text-xl font-bold text-gray-800'>Conversation Messages</h2>
          <span className='text-sm text-gray-500 truncate max-w-[50%]'>
            {title}
            <span className='ml-2 text-xs text-gray-400'>#{conversationId}</span>
          </span>
        </div>
        {creditUsage && (
          <div className='mt-2'>
            <CreditUsageBanner usage={creditUsage} />
          </div>
        )}
      </div>

      {isLoading ? (
        <div className='flex-1 flex items-center justify-center'>
          <Spinner size='lg' />
          <span className='ml-3 text-gray-500 select-none'>Loading...</span>
        </div>
      ) : messages.length === 0 ? (
        <div className='flex-1 flex items-center justify-center text-gray-500'>
          No messages found
        </div>
      ) : (
        <div className='flex-1 overflow-auto p-6'>
          <div className='space-y-6 max-w-[720px] mx-auto'>
            {messages
              .filter(msg => hasRenderableContent(msg))
              .map(msg => {
                const parsed = parseMessageContent(msg)
                const formattedTime = formatDate(msg.created_time)
                return (
                  <div key={msg.id} className='flex flex-col gap-2'>
                    {msg.role === 'user' ? (
                      <div className='flex flex-col items-end'>
                        <div className='flex items-center gap-2 max-w-[80%]'>
                          <InitialsAvatar name='User' size='xs' />
                          <Typography
                            fontFamily='geist'
                            fontSize='bodySm'
                            fontWeight='semibold'
                            className='text-tokens-neutral-text-secondary'
                          >
                            User
                          </Typography>
                          {formattedTime && (
                            <Typography
                              fontFamily='geist'
                              fontSize='bodySm'
                              fontWeight='normal'
                              className='text-tokens-neutral-text-tertiary'
                            >
                              {formattedTime}
                            </Typography>
                          )}
                        </div>
                        <div className='bg-[rgba(0,0,0,0.04)] p-4 rounded-3xl max-w-[80%]'>
                          {/* User content renders as plain text, never markdown
                              — mirrors UserMessage and avoids the nested-list
                              overflow that crashed this view (conversation
                              42719). Assistant content below keeps markdown. */}
                          <Typography
                            fontFamily='geist'
                            fontSize='bodyMdMedium'
                            fontWeight='medium'
                            className='text-gray-900 whitespace-pre-wrap break-words'
                          >
                            {parsed.textContent || ''}
                          </Typography>
                        </div>
                      </div>
                    ) : (
                      <div className='flex flex-col'>
                        <div className='flex items-center gap-2 mb-1'>
                          <InitialsAvatar
                            name='Curie'
                            size='xs'
                            className='bg-tokens-status-link-base-2'
                          />
                          <Typography
                            fontFamily='geist'
                            fontSize='bodySm'
                            fontWeight='semibold'
                            className='text-tokens-neutral-text-secondary'
                          >
                            Curie
                          </Typography>
                          {formattedTime && (
                            <Typography
                              fontFamily='geist'
                              fontSize='bodySm'
                              fontWeight='normal'
                              className='text-tokens-neutral-text-tertiary'
                            >
                              {formattedTime}
                            </Typography>
                          )}
                        </div>
                        {parsed.thinkingBlocks.length > 0 && (
                          <div className='mb-2 pl-8'>
                            {parsed.thinkingBlocks.map((thinking, idx) => (
                              <div
                                key={idx}
                                className='flex items-center gap-2 text-sm text-gray-500 italic py-1'
                              >
                                <svg
                                  className='w-4 h-4 text-gray-400'
                                  fill='none'
                                  stroke='currentColor'
                                  viewBox='0 0 24 24'
                                >
                                  <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
                                  />
                                </svg>
                                <span>{thinking.title}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {parsed.textContent && (
                          <div className='pl-8'>
                            <ChatMarkdown className='text-gray-900'>
                              {parsed.textContent}
                            </ChatMarkdown>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}
    </div>
  )
}
